/// <amd-module name="InstantSearch/Shared/InstantSearchProviders/BaseInstantSearchProvider" />
import { IReadonlyObservableArray, ObservableArray } from "VSS/Core/Observable";
import { VssService } from "VSS/Platform/Context";
import { IVssContributionService } from "VSS/Platform/Contribution";
import { IVssScenario } from "VSS/Platform/Performance";
import { IMenuItem } from "VSSUI/Menu";
import { AllHubs } from "InstantSearch/Shared/Constants";
import { RequestManager } from "InstantSearch/Shared/RequestManager";
import { IMenuProviderContribution, IMenuServiceContext, IProviderInvocationProps } from "InstantSearch/Shared/Types";
import { getQueryTextWithWildCardChar } from "InstantSearch/Shared/Utils";

/**
 * Base instant search provider.
 */
export class BaseInstantSearchProvider<T> extends VssService {
    private readonly _requestManager = new RequestManager();

    protected _menuItems = new ObservableArray<IMenuItem>();
    protected _contribution: IMenuProviderContribution | undefined;
    protected _providerInvocationProps: IProviderInvocationProps[];
    protected _isHubSupportedByDefault: boolean | undefined;
    protected _groupKey: string;
    protected _rank: number;

    public clearMenuItems(): void {
        this._menuItems.splice(0, this._menuItems.length);
        this._isHubSupportedByDefault = undefined;
    }

    public getMenuItems(menuId: string, context: IMenuServiceContext): IReadonlyObservableArray<IMenuItem> | null {
        const { searchInput } = context;
        const invokeProvider = this.shouldInvokeProvider(context);
        const trimmedSearchText = searchInput.queryText().trim();
        const searchText = getQueryTextWithWildCardChar(trimmedSearchText);

        return this._getMenuItems(menuId, context, invokeProvider, searchText);
    }

    public isHubSupportedByDefault(hubName: string): boolean {
        if (this._isHubSupportedByDefault == undefined) {
            const supportedHubs = this._contribution ? this._contribution.defaultSupportedHubs || [AllHubs] : [];
            this._isHubSupportedByDefault = supportedHubs.findIndex(hub => hub === hubName || hub === AllHubs) !== -1;
        }

        return this._isHubSupportedByDefault;
    }

    public shouldInvokeProvider(context: IMenuServiceContext): boolean {
        const id = this._contribution ? this._contribution.menuId : "";
        const searchProviderId = this._contribution ? this._contribution.searchProviderId : "";
        const { providerMenuIds, hubName, invokedProvidersProps, searchInput } = context;
        const isProviderInvoked = providerMenuIds.findIndex(providerId => providerId === id) !== -1;
        const isHubSupported = !!invokedProvidersProps || this.isHubSupportedByDefault(hubName);
        if (
            searchProviderId &&
            invokedProvidersProps &&
            this._providerInvocationProps.findIndex(prop => prop.key === invokedProvidersProps.key) !== -1
        ) {
            // If current provider is invoked and a search provider is associated with it 
            // (i.e., has immersive search experience), then set it as help search provider,
            // so that when enter is pressed, it navigates to the corresponding search page tab.
            searchInput.setHelpSearchProviderId(searchProviderId);
        }

        return isProviderInvoked && isHubSupported;
    }

    protected getSuggestions = (
        searchText: string,
        context: IMenuServiceContext,
        telemetryEventName: string,
        ttiSearchScenarioName?: string,
        subSequentSearchScenarioName?: string
    ): void => {
        const currentTime = performance.now();
        const { telemetryWriter, invokedProvidersProps } = context;
        const requestId = this._requestManager.newRequestId;
        let recordTTIScenario = false;
        let scenario: IVssScenario | undefined = undefined;
        if (ttiSearchScenarioName && subSequentSearchScenarioName) {
            recordTTIScenario = true;
            scenario = telemetryWriter.notifySearchStarted(telemetryEventName, ttiSearchScenarioName, subSequentSearchScenarioName);
            scenario.addProperties({ "isQuickNavigationMode": !!invokedProvidersProps });
        }

        this._fetchResults(searchText, context).then(
            (data: T) => {
                const totalTime = performance.now() - currentTime;
                telemetryWriter.publish(telemetryEventName, { timeToFetch: totalTime });

                if (this._requestManager.isValidRequest(requestId)) {
                    this._onFetchSuccess(searchText, context, data, scenario);
                }

                if (recordTTIScenario && scenario) {
                    telemetryWriter.notifyResultsRendered(scenario);
                }
            },
            (error: Error) => {
                this._onFetchFail(context, error, scenario);

                if (recordTTIScenario && scenario) {
                    telemetryWriter.notifySearchFailed(scenario, error);
                }
            }
        );
    };

    protected _initContribution(contributionId: string): void {
        const contributionService = this.pageContext.getService<IVssContributionService>("IVssContributionService");
        const contribution = contributionService.getContribution<IMenuProviderContribution>(contributionId);
        if (contribution) {
            this._contribution = contribution;
            this._groupKey = contribution.groupKey;
            this._rank = contribution.rank;
            this._providerInvocationProps = contribution.providerInvocationProps || [];
        }
    }

    protected _fetchResults(searchText: string, context: IMenuServiceContext): Promise<T> {
        return Promise.resolve({} as T);
    }

    protected _getMenuItems(
        menuId: string,
        context: IMenuServiceContext,
        invokeProvider: boolean,
        searchText: string
    ): IReadonlyObservableArray<IMenuItem> | null {
        return null;
    }

    protected _onFetchSuccess(searchText: string, context: IMenuServiceContext, data: T, scenario?: IVssScenario): void {
        return;
    }

    protected _onFetchFail(context: IMenuServiceContext, error: Error, scenario?: IVssScenario): void {
        return;
    }
}
